/**
 * Utility to completely disable PWA functionality and clear all caches
 */

export const disablePWA = async (): Promise<void> => {
  console.log('PWA Cleanup: Starting PWA disable process');

  try {
    // 1. Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      console.log('PWA Cleanup: Found', registrations.length, 'service worker registrations');
      
      for (const registration of registrations) {
        console.log('PWA Cleanup: Unregistering service worker:', registration.scope);
        await registration.unregister();
      }
    }

    // 2. Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      console.log('PWA Cleanup: Found caches:', cacheNames);
      
      for (const cacheName of cacheNames) {
        console.log('PWA Cleanup: Deleting cache:', cacheName);
        await caches.delete(cacheName);
      }
    }

    // 3. Clear localStorage items related to PWA
    const pwaKeys = [
      'workbox-precache',
      'workbox-runtime',
      'pwa-update-available',
      'sw-update-available'
    ];
    
    pwaKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log('PWA Cleanup: Removing localStorage key:', key);
        localStorage.removeItem(key);
      }
    });

    // 4. Clear sessionStorage items related to PWA
    pwaKeys.forEach(key => {
      if (sessionStorage.getItem(key)) {
        console.log('PWA Cleanup: Removing sessionStorage key:', key);
        sessionStorage.removeItem(key);
      }
    });

    // 5. Clear any app badges
    if ('clearAppBadge' in navigator) {
      try {
        await (navigator as any).clearAppBadge();
        console.log('PWA Cleanup: Cleared app badge');
      } catch (error) {
        console.log('PWA Cleanup: Could not clear app badge:', error);
      }
    }

    console.log('PWA Cleanup: PWA disable process completed successfully');
  } catch (error) {
    console.error('PWA Cleanup: Error during PWA disable process:', error);
    throw error;
  }
};

export const registerCleanupServiceWorker = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    console.log('PWA Cleanup: Service workers not supported');
    return;
  }

  try {
    console.log('PWA Cleanup: Registering cleanup service worker');
    
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });

    console.log('PWA Cleanup: Cleanup service worker registered:', registration.scope);

    // Listen for messages from the service worker
    navigator.serviceWorker.addEventListener('message', async (event) => {
      if (event.data && event.data.type === 'UNREGISTER_SW') {
        console.log('PWA Cleanup: Received unregister request from service worker');
        
        // Wait a bit for the service worker to finish cleaning
        setTimeout(async () => {
          try {
            await registration.unregister();
            console.log('PWA Cleanup: Service worker unregistered successfully');
          } catch (error) {
            console.error('PWA Cleanup: Error unregistering service worker:', error);
          }
        }, 1000);
      }
    });

    // Force the service worker to activate immediately
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

  } catch (error) {
    console.error('PWA Cleanup: Error registering cleanup service worker:', error);
  }
};

export const preventPWAInstallation = (): void => {
  // Prevent PWA installation prompts
  window.addEventListener('beforeinstallprompt', (event) => {
    console.log('PWA Cleanup: Preventing PWA installation prompt');
    event.preventDefault();
    return false;
  });

  // Remove any existing install prompts
  if ('getInstalledRelatedApps' in navigator) {
    (navigator as any).getInstalledRelatedApps().then((apps: any[]) => {
      if (apps.length > 0) {
        console.log('PWA Cleanup: Found installed related apps:', apps);
      }
    }).catch((error: any) => {
      console.log('PWA Cleanup: Could not check installed apps:', error);
    });
  }
};

// Complete PWA cleanup function
export const completePWACleanup = async (): Promise<void> => {
  console.log('PWA Cleanup: Starting complete PWA cleanup');
  
  try {
    // Step 1: Register cleanup service worker to clear caches
    await registerCleanupServiceWorker();
    
    // Step 2: Wait for service worker to do its work
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Step 3: Disable PWA functionality
    await disablePWA();
    
    // Step 4: Prevent future PWA installation
    preventPWAInstallation();
    
    console.log('PWA Cleanup: Complete PWA cleanup finished');
  } catch (error) {
    console.error('PWA Cleanup: Error during complete cleanup:', error);
  }
}; 